const passthroughEffect = (scoreState) => scoreState;

const letterAdditiveEffect = (value) => (scoreState) => ({
  ...scoreState,
  letterBonus: (scoreState.letterBonus || 0) + value,
});

export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export const normalizeLetterPool = (letterPool, fallbackPool = ALPHABET) => {
  const fallback = Array.isArray(fallbackPool) && fallbackPool.length ? fallbackPool : ALPHABET;
  const normalized = (Array.isArray(letterPool) ? letterPool : [])
    .map((entry) => String(entry || '').trim().toUpperCase())
    .filter((entry) => /^[A-Z]$/.test(entry));

  return normalized.length ? [...new Set(normalized)] : [...fallback];
};

const getPersistentLetterXConfig = (runSettings) => {
  const defaults = {
    letterPool: ALPHABET,
    bonus: 2,
    scope: 'game',
  };

  const config = runSettings?.persistentEffects?.letterXPlusOne || {};

  return {
    letterPool: normalizeLetterPool(config.letterPool, defaults.letterPool),
    bonus: Number.isFinite(Number(config.bonus)) ? Number(config.bonus) : defaults.bonus,
    scope: String(config.scope || defaults.scope),
  };
};

/**
 * Function signature used by scoring modifiers.
 *
 * @callback ScoreEffectFn
 * @param {{
 *   letterMultiplier?: number,
 *   wordMultiplier?: number,
 *   letterBonus?: number,
 *   wordBonus?: number,
 * }} scoreState Mutable-like score accumulator represented as an immutable object.
 * @param {{
 *   letter?: string,
 *   tileId?: string,
 *   [key: string]: unknown,
 * }} [context] Optional scoring context for conditional effects.
 * @returns {{
 *   letterMultiplier?: number,
 *   wordMultiplier?: number,
 *   letterBonus?: number,
 *   wordBonus?: number,
 * }} Updated score state.
 */

/**
 * Descriptor for a game-level effect granted by a modifier.
 *
 * @typedef {Object} GrantedGameEffect
 * @property {string} type Effect discriminator (for example GLOBAL_LETTER_BONUS).
 * @property {string} [label] Human-readable label used in UI and score breakdowns.
 * @property {'game' | string} [scope] Lifecycle scope, typically game.
 * @property {string} [letter] Optional target letter for letter-specific effects.
 * @property {number} [bonus] Optional numeric bonus value.
 * @property {number} [bonusSubmits] Optional submits bonus per round.
 * @property {number} [bonusDiscards] Optional discards bonus per round.
 * @property {number} [bonusRerolls] Optional rerolls bonus per shop stage.
 * @property {number} [bonusPicks] Optional shop picks bonus per shop stage.
 * @property {string} [rarity] Optional rarity key for rarity-specific effects.
 * @property {number} [bonusChance] Optional additive chance bonus for a rarity.
 * @property {number} [bonusPerVowel] Optional per-vowel bonus (legacy/custom effects).
 */

/**
 * Constructor config for ShopOptionModifierType.
 *
 * @typedef {Object} ShopOptionModifierConfig
 * @property {string} id Canonical unique identifier. Stored uppercased.
 * @property {string} [label] Compact badge/title text.
 * @property {string} [badge] Optional short badge text for UI display; falls back to label if not provided.
 * @property {string} [description] Longer explanation shown in UI/body text.
 * @property {'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | string} [rarity='common']
 * Relative rarity used by shop option generation.
 * @property {'tile' | 'game' | string} [category='tile'] Modifier category.
 * @property {number} [scoreBonus]
 * UI/display metadata for tile score previews. This is not the authoritative scoring hook.
 * @property {boolean} [collapseIntoScoreDisplay]
 * When true, UI can fold this modifier into displayed point math instead of a separate badge.
 * @property {ScoreEffectFn} [applyEffect]
 * Primary scoring effect function. Used by engine evaluation when no explicit onLetterScore is provided.
 * @property {ScoreEffectFn} [onLetterScore]
 * Optional scoring hook specialized for letter-phase application; defaults to applyEffect.
 * @property {GrantedGameEffect[] | ((context: {
 *   runSettings?: unknown,
 *   rng?: () => number,
 *   tile?: unknown,
 *   modifierId?: string,
 * }) => GrantedGameEffect[])} [grantsGameEffects]
 * Optional static/dynamic persistent effects granted by this modifier.
 * @property {(context: {
 *   grantedEffects?: GrantedGameEffect[],
 *   modifierId?: string,
 *   defaultLabel?: string,
 * }) => string} [shopLabelResolver]
 * Optional formatter used by shop UI generation to produce a dynamic label.
 * @property {{
 *   upgradeWeight?: number,
 *   newTileWeight?: number,
 *   gameWeight?: number,
 * }} [shop]
 * Weights that control which shop generators are allowed to pick this modifier.
 */

/**
 * Canonical modifier model shared by tile and game modifier definitions.
 *
 * Design notes:
 * - scoreBonus is display metadata for tile preview/inspection UI.
 * - applyEffect/onLetterScore are the authoritative scoring hooks used by the scoring engine.
 * - grantsGameEffects enables persistent run-wide effects from game modifiers.
 */
export class ShopOptionModifierType {
  /**
   * @param {ShopOptionModifierConfig} config Modifier configuration payload.
   */
  constructor({
    id,
    label,
    badge,
    description,
    rarity = 'common',
    category = 'tile',
    scoreBonus,
    collapseIntoScoreDisplay,
    applyEffect,
    onLetterScore,
    grantsGameEffects,
    shopLabelResolver,
    boardSquareModifierId,
    shop = {},
  }) {
    this.id = String(id || '').trim().toUpperCase();
    this.label = label || '';
    this.badge = badge || "";
    this.description = description || '';
    this.rarity = String(rarity || 'common').trim().toLowerCase() || 'common';
    this.category = String(category || 'tile').trim().toLowerCase();
    this.scoreBonus = Number.isFinite(scoreBonus) ? scoreBonus : undefined;
    this.collapseIntoScoreDisplay = Boolean(collapseIntoScoreDisplay);
    this.applyEffect = typeof applyEffect === 'function' ? applyEffect : passthroughEffect;
    this.onLetterScore = typeof onLetterScore === 'function' ? onLetterScore : this.applyEffect;
    this.grantsGameEffects =
      typeof grantsGameEffects === 'function' || Array.isArray(grantsGameEffects)
        ? grantsGameEffects
        : undefined;
    this.shopLabelResolver = typeof shopLabelResolver === 'function' ? shopLabelResolver : undefined;
    this.boardSquareModifierId = boardSquareModifierId ? String(boardSquareModifierId).toUpperCase() : undefined;
    this.shop = {
      upgradeWeight: Math.max(0, Number(shop.upgradeWeight) || 0),
      newTileWeight: Math.max(0, Number(shop.newTileWeight) || 0),
      gameWeight: Math.max(0, Number(shop.gameWeight) || 0),
      boardWeight: Math.max(0, Number(shop.boardWeight) || 0),
    };
  }

  toDefinition() {
    return {
      label: this.label,
      badge: this.badge,
      description: this.description,
      rarity: this.rarity,
      category: this.category,
      scoreBonus: this.scoreBonus,
      collapseIntoScoreDisplay: this.collapseIntoScoreDisplay,
      applyEffect: this.applyEffect,
      onLetterScore: this.onLetterScore,
      grantsGameEffects: this.grantsGameEffects,
      shopLabelResolver: this.shopLabelResolver,
      boardSquareModifierId: this.boardSquareModifierId,
    };
  }
}

const resolveLetterBonusShopLabel = ({ grantedEffects = [], defaultLabel = '' }) => {
  const letterBonusEffect = (Array.isArray(grantedEffects) ? grantedEffects : []).find(
    (effect) => effect?.type === 'GLOBAL_LETTER_BONUS' && effect?.letter
  );

  if (!letterBonusEffect) {
    return defaultLabel;
  }

  return `All ${String(letterBonusEffect.letter).toUpperCase()} tiles +${Number(letterBonusEffect.bonus || 0)}`;
};

const resolveLetterMultiplierShopLabel = ({ grantedEffects = [], defaultLabel = '' }) => {
  const letterMultiplierEffect = (Array.isArray(grantedEffects) ? grantedEffects : []).find(
    (effect) => effect?.type === 'GLOBAL_WORD_MULTIPLIER_FOR_LETTER' && effect?.letter
  );

  if (!letterMultiplierEffect) {
    return defaultLabel;
  }

  return `All ${String(letterMultiplierEffect.letter).toUpperCase()} tiles x${Number(letterMultiplierEffect.multiplier || 1)}`;
};

export class TileModifierType extends ShopOptionModifierType {
  constructor(config) {
    super({ ...config, category: 'tile' });
  }
}

export class GameModifierType extends ShopOptionModifierType {
  constructor(config) {
    super({ ...config, category: 'game', applyEffect: passthroughEffect, onLetterScore: passthroughEffect });
  }
}

export class BoardModifierType extends ShopOptionModifierType {
  constructor(config) {
    super({ ...config, category: 'board', applyEffect: passthroughEffect, onLetterScore: passthroughEffect });
  }
}

const createTileModifier = (config) => new TileModifierType(config);
const createGameModifier = (config) => new GameModifierType(config);
const createBoardModifier = (config) => new BoardModifierType(config);

export const SHOP_OPTION_MODIFIER_TYPES = [
  createTileModifier({
    id: 'NORMAL',
    label: '',
    description: 'No tile modifier effect.',
    rarity: 'common',
    applyEffect: passthroughEffect,
    shop: {
      newTileWeight: 0,
    },
  }),
  createTileModifier({
    id: 'TILE_PLUS_1',
    label: '+1 Score',
    badge: "",
    description: 'Adds +1 to this tile letter score.',
    rarity: 'common',
    scoreBonus: 1,
    collapseIntoScoreDisplay: true,
    applyEffect: letterAdditiveEffect(1),
    shop: {
      upgradeWeight: 24,
      newTileWeight: 14,
    },
  }),
  createTileModifier({
    id: 'TILE_PLUS_2',
    label: '+2 Score',
    badge: "",
    description: 'Adds +2 to this tile letter score.',
    rarity: 'uncommon',
    scoreBonus: 2,
    collapseIntoScoreDisplay: true,
    applyEffect: letterAdditiveEffect(2),
    shop: {
      upgradeWeight: 48,
      newTileWeight: 9,
    },
  }),
  createTileModifier({
    id: 'WORD_X_2',
    label: 'Word ×2',
    badge: "x2",
    description: 'Multiplies the total word score by 2.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    shop: {
      upgradeWeight: 22,
      newTileWeight: 4,
    },
  }),
  createTileModifier({
    id: 'WORD_X_4',
    label: 'Word ×4',
    badge: "x4",
    description: 'Multiplies the total word score by 4.',
    rarity: 'epic',
    applyEffect: passthroughEffect,
    shop: {
      upgradeWeight: 3,
      newTileWeight: 0,
    },
  }),
  createTileModifier({
    id: 'WORD_X_8',
    label: 'Word ×8',
    badge: "x8",
    description: 'Multiplies the total word score by 8.',
    rarity: 'legendary',
    applyEffect: passthroughEffect,
    shop: {
      upgradeWeight: 1,
      newTileWeight: 0,
    },
  }),
  createTileModifier({
    id: 'LETTER_X_2',
    label: 'x2 Letter Score',
    badge: 'x2L',
    description: 'Doubles this tile\'s letter score.',
    rarity: 'uncommon',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState) => ({
      ...scoreState,
      letterMultiplier: (scoreState.letterMultiplier || 1) * 2,
    }),
    shop: {
      upgradeWeight: 20,
      newTileWeight: 5,
    },
  }),
  createTileModifier({
    id: 'LETTER_X_3',
    label: 'x3 Letter Score',
    badge: 'x3L',
    description: 'Triples this tile\'s letter score.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState) => ({
      ...scoreState,
      letterMultiplier: (scoreState.letterMultiplier || 1) * 3,
    }),
    shop: {
      upgradeWeight: 12,
      newTileWeight: 2,
    },
  }),
  createTileModifier({
    id: 'WORD_BONUS_5',
    label: '+5 Word',
    badge: '+5W',
    description: 'Adds +5 flat bonus to the word score.',
    rarity: 'uncommon',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState) => ({
      ...scoreState,
      wordBonus: (scoreState.wordBonus || 0) + 5,
    }),
    shop: {
      upgradeWeight: 18,
      newTileWeight: 6,
    },
  }),
  createTileModifier({
    id: 'WORD_BONUS_15',
    label: '+15 Word',
    badge: '+15W',
    description: 'Adds +15 flat bonus to the word score.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState) => ({
      ...scoreState,
      wordBonus: (scoreState.wordBonus || 0) + 15,
    }),
    shop: {
      upgradeWeight: 10,
      newTileWeight: 2,
    },
  }),
  createTileModifier({
    id: 'SHORT_WORD_TRIPLE',
    label: '×3 If Short (3-)',
    badge: 'x3S',
    description: 'Triples the word score if the word has 3 or fewer tiles.',
    rarity: 'epic',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => {
      if ((context?.wordLength || 0) > 3) return scoreState;
      return { ...scoreState, wordMultiplier: (scoreState.wordMultiplier || 1) * 3 };
    },
    shop: {
      upgradeWeight: 0,
      newTileWeight: 0,
    },
  }),
  createTileModifier({
    id: 'LONG_WORD_SURGE',
    label: '+20 Long (5+)',
    badge: '+20L',
    description: 'Adds +20 flat bonus to the word score if the word has 5 or more tiles.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => {
      if ((context?.wordLength || 0) < 5) return scoreState;
      return { ...scoreState, wordBonus: (scoreState.wordBonus || 0) + 20 };
    },
    shop: {
      upgradeWeight: 18,
      newTileWeight: 4,
    },
  }),
  createTileModifier({
    id: 'PLUS_PER_TILE',
    label: '+1 Score Per Letter',
    badge: '+N',
    description: 'Adds +1 letter score for each tile in the word.',
    rarity: 'uncommon',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => ({
      ...scoreState,
      letterBonus: (scoreState.letterBonus || 0) + Math.max(0, Number(context?.wordLength) || 0),
    }),
    shop: {
      upgradeWeight: 28,
      newTileWeight: 8,
    },
  }),
  createTileModifier({
    id: 'PERFECT_FIVE',
    label: '×5 If Five',
    badge: 'x5',
    description: 'Multiplies the word score by 5 if the word has exactly 5 tiles.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => {
      if ((context?.wordLength || 0) !== 5) return scoreState;
      return { ...scoreState, wordMultiplier: (scoreState.wordMultiplier || 1) * 5 };
    },
    shop: {
      upgradeWeight: 18,
      newTileWeight: 4,
    },
  }),
  createTileModifier({
    id: 'ESCALATING_BONUS',
    label: '+2 Per Tile',
    badge: '+2N',
    description: 'Adds +2 letter bonus for each tile in the word beyond the first.',
    rarity: 'uncommon',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => ({
      ...scoreState,
      letterBonus: (scoreState.letterBonus || 0) + Math.max(0, ((context?.wordLength || 1) - 1) * 2),
    }),
    shop: {
      upgradeWeight: 28,
      newTileWeight: 8,
    },
  }),
  createTileModifier({
    id: 'WORD_X_WORD_LENGTH',
    label: '× Word Length',
    badge: 'xN',
    description: 'Multiplies the word score by the number of tiles in the word.',
    rarity: 'legendary',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => ({
      ...scoreState,
      wordMultiplier: (scoreState.wordMultiplier || 1) * Math.max(1, Number(context?.wordLength) || 1),
    }),
    shop: {
      upgradeWeight: 6,
      newTileWeight: 0,
    },
  }),
  createTileModifier({
    id: 'LENGTH_SCALER',
    label: '+ Word Length Score',
    badge: '+LW',
    description: 'Adds a flat bonus to the word score equal to the number of tiles in the word.',
    rarity: 'uncommon',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => ({
      ...scoreState,
      wordBonus: (scoreState.wordBonus || 0) + Math.max(0, Number(context?.wordLength) || 0),
    }),
    shop: {
      upgradeWeight: 22,
      newTileWeight: 6,
    },
  }),
  createTileModifier({
    id: 'BOARD_GROWTH',
    label: '+1 Score Per 8 Tiles',
    badge: '+BB',
    description: 'Adds +1 letter bonus for every 8 tiles already on the board.',
    rarity: 'rare',
    applyEffect: passthroughEffect,
    onLetterScore: (scoreState, context) => ({
      ...scoreState,
      letterBonus: (scoreState.letterBonus || 0) + Math.floor((context?.boardTileCount || 0) / 8),
    }),
    shop: {
      upgradeWeight: 14,
      newTileWeight: 3,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_LETTER_X_PLUS_1',
    label: '+2 Score',
    description: 'Adds +2 score to a letter for the rest of the game.',
    rarity: 'epic',
    grantsGameEffects: ({ runSettings, rng = Math.random }) => {
      const config = getPersistentLetterXConfig(runSettings);
      const letterPool = normalizeLetterPool(config.letterPool);
      const selectedIndex = Math.floor((rng?.() ?? Math.random()) * letterPool.length);
      const selectedLetter = letterPool[selectedIndex] || 'S';
      const bonus = Number(config.bonus || 0);

      return [
        {
          type: 'GLOBAL_LETTER_BONUS',
          label: `${selectedLetter} +${bonus} (Game)`,
          letter: selectedLetter,
          bonus,
          scope: config.scope || 'game',
        },
      ];
    },
    shopLabelResolver: resolveLetterBonusShopLabel,
    shop: {
      gameWeight: 10,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_SUBMIT_PLUS_1',
    label: '+1 Submit',
    description: 'Adds +1 submit to each round for the rest of the game.',
    rarity: 'legendary',
    grantsGameEffects: [
      {
        type: 'GLOBAL_ROUND_SUBMITS_BONUS',
        label: 'Round Submits +1 (Game)',
        bonusSubmits: 1,
        scope: 'game',
      },
    ],
    shop: {
      gameWeight: 3,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_LETTER_X_X2',
    label: 'x2 score',
    description: 'Doubles total word score for words containing a letter, for the rest of the game.',
    rarity: 'legendary',
    grantsGameEffects: ({ runSettings, rng = Math.random }) => {
      const config = getPersistentLetterXConfig(runSettings);
      const letterPool = normalizeLetterPool(config.letterPool);
      const selectedIndex = Math.floor((rng?.() ?? Math.random()) * letterPool.length);
      const selectedLetter = letterPool[selectedIndex] || 'S';

      return [
        {
          type: 'GLOBAL_WORD_MULTIPLIER_FOR_LETTER',
          label: `${selectedLetter} words x2 (Game)`,
          letter: selectedLetter,
          multiplier: 2,
          scope: config.scope || 'game',
        },
      ];
    },
    shopLabelResolver: resolveLetterMultiplierShopLabel,
    shop: {
      gameWeight: 3,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_DISCARD_PLUS_1',
    label: '+1 Discard',
    description: 'Adds +1 discard to each round for the rest of the game.',
    rarity: 'epic',
    grantsGameEffects: [
      {
        type: 'GLOBAL_ROUND_DISCARDS_BONUS',
        label: 'Round Discards +1 (Game)',
        bonusDiscards: 1,
        scope: 'game',
      },
    ],
    shop: {
      gameWeight: 5,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_SHOP_REROLL_PLUS_1',
    label: '+1 Shop Reroll',
    description: 'Adds +1 shop reroll per shop stage for the rest of the game.',
    rarity: 'epic',
    grantsGameEffects: [
      {
        type: 'GLOBAL_SHOP_REROLLS_BONUS',
        label: 'Shop Rerolls +1 (Game)',
        bonusRerolls: 1,
        scope: 'game',
      },
    ],
    shop: {
      gameWeight: 5,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_SHOP_PICKS_PLUS_1',
    label: '+1 Shop Pick',
    description: 'Adds +1 shop pick per shop stage for the rest of the game.',
    rarity: 'epic',
    grantsGameEffects: [
      {
        type: 'GLOBAL_SHOP_PICKS_BONUS',
        label: 'Shop Picks +1 (Game)',
        bonusPicks: 1,
        scope: 'game',
      },
    ],
    shop: {
      gameWeight: 5,
    },
  }),
  createGameModifier({
    id: 'PERSISTENT_LUCKY_FINDS',
    label: 'Lucky Finds',
    description: 'Increases the chance of rarer shop options for the rest of the game.',
    rarity: 'epic',
    grantsGameEffects: [
      { type: 'GLOBAL_SHOP_RARITY_CHANCE_BONUS', label: 'Lucky Finds: Rare',      rarity: 'rare',      bonusChance: 6, scope: 'game' },
      { type: 'GLOBAL_SHOP_RARITY_CHANCE_BONUS', label: 'Lucky Finds: Epic',      rarity: 'epic',      bonusChance: 4, scope: 'game' },
      { type: 'GLOBAL_SHOP_RARITY_CHANCE_BONUS', label: 'Lucky Finds: Legendary', rarity: 'legendary', bonusChance: 2, scope: 'game' },
    ],
    shop: {
      gameWeight: 5,
    },
  }),
  createBoardModifier({
    id: 'BOARD_DOUBLE_LETTER',
    label: 'Double Letter Square',
    description: 'Permanently upgrades a board square to Double Letter (DL).',
    rarity: 'uncommon',
    boardSquareModifierId: 'DL',
    shop: { boardWeight: 14 },
  }),
  createBoardModifier({
    id: 'BOARD_TRIPLE_LETTER',
    label: 'Triple Letter Square',
    description: 'Permanently upgrades a board square to Triple Letter (TL).',
    rarity: 'rare',
    boardSquareModifierId: 'TL',
    shop: { boardWeight: 9 },
  }),
  createBoardModifier({
    id: 'BOARD_DOUBLE_WORD',
    label: 'Double Word Square',
    description: 'Permanently upgrades a board square to Double Word (DW).',
    rarity: 'rare',
    boardSquareModifierId: 'DW',
    shop: { boardWeight: 9 },
  }),
  createBoardModifier({
    id: 'BOARD_TRIPLE_WORD',
    label: 'Triple Word Square',
    description: 'Permanently upgrades a board square to Triple Word (TW).',
    rarity: 'epic',
    boardSquareModifierId: 'TW',
    shop: { boardWeight: 4 },
  }),
  createBoardModifier({
    id: 'BOARD_QUAD_LETTER',
    label: 'Quad Letter Square',
    description: 'Permanently upgrades a board square to Quad Letter (4L).',
    rarity: 'legendary',
    boardSquareModifierId: '4L',
    shop: { boardWeight: 1 },
  }),
  createBoardModifier({
    id: 'BOARD_QUAD_WORD',
    label: 'Quad Word Square',
    description: 'Permanently upgrades a board square to Quad Word (4W).',
    rarity: 'legendary',
    boardSquareModifierId: '4W',
    shop: { boardWeight: 1 },
  }),
];

export const TILE_MODIFIER_TYPES = SHOP_OPTION_MODIFIER_TYPES.filter(
  (modifierType) => modifierType.category === 'tile'
);

export const GAME_MODIFIER_TYPES = SHOP_OPTION_MODIFIER_TYPES.filter(
  (modifierType) => modifierType.category === 'game'
);

const SHOP_OPTION_MODIFIER_TYPE_MAP = SHOP_OPTION_MODIFIER_TYPES.reduce((acc, type) => {
  acc[type.id] = type;
  return acc;
}, {});

export const SHOP_OPTION_MODIFIER_DEFINITIONS = SHOP_OPTION_MODIFIER_TYPES.reduce((acc, type) => {
  acc[type.id] = type.toDefinition();
  return acc;
}, {});

export const TILE_MODIFIER_DEFINITIONS = TILE_MODIFIER_TYPES.reduce((acc, type) => {
  acc[type.id] = type.toDefinition();
  return acc;
}, {});

export const normalizeShopOptionModifierId = (modifierId) => {
  const normalized = String(modifierId || '').trim().toUpperCase();
  if (!normalized) {
    return '';
  }

  return normalized;
};

export const normalizeTileModifierId = normalizeShopOptionModifierId;

export const createModifierLookup = (definitionMap, label) => (modifierId) => {
  const normalizedId = normalizeShopOptionModifierId(modifierId);
  const meta = definitionMap[normalizedId];
  if (!meta) {
    const error = new Error(`Unknown ${label}: ${modifierId}`);
    console.error(error);
    throw error;
  }
  return meta;
};

export const normalizeTileModifierList = (modifierIds) => {
  const normalized = (Array.isArray(modifierIds) ? modifierIds : [modifierIds])
    .filter(Boolean)
    .map((modifierId) => normalizeTileModifierId(modifierId))
    .filter(Boolean);

  if (!normalized.length) {
    return ['NORMAL'];
  }

  const unique = [...new Set(normalized)];
  const withoutNormal = unique.filter((modifierId) => modifierId !== 'NORMAL');

  return withoutNormal.length ? withoutNormal : ['NORMAL'];
};

export const getShopOptionModifierMeta = createModifierLookup(
  SHOP_OPTION_MODIFIER_DEFINITIONS,
  'shop option modifier'
);

export const getTileModifierMeta = createModifierLookup(
  SHOP_OPTION_MODIFIER_DEFINITIONS,
  'tile modifier'
);

export const getShopOptionModifierRarity = (modifierId) => {
  const normalizedModifierId = normalizeShopOptionModifierId(modifierId);
  return SHOP_OPTION_MODIFIER_TYPE_MAP[normalizedModifierId]?.rarity || 'common';
};

export const getTileModifierRarity = getShopOptionModifierRarity;

export const SCORE_MULTIPLIER_MODIFIER_VALUES = {
  WORD_X_2: 2,
  WORD_X_4: 4,
  WORD_X_8: 8,
};

export const normalizeTileModifiers = (modifiers) => ({
  score: Number(modifiers?.score) || 0,
  multiplier: Math.max(1, Number(modifiers?.multiplier) || 1),
  extras: (Array.isArray(modifiers?.extras) ? modifiers.extras : [])
    .map((id) => String(id || '').trim().toUpperCase())
    .filter((id) => id && id !== 'NORMAL'),
  ids: (Array.isArray(modifiers?.ids) ? modifiers.ids : [])
    .map((id) => String(id || '').trim().toUpperCase())
    .filter((id) => id && id !== 'NORMAL'),
});

export const applyModifierIdToTileModifiers = (tileModifiers, modifierId) => {
  const current = normalizeTileModifiers(tileModifiers);
  const normalizedId = normalizeTileModifierId(modifierId);

  if (!normalizedId || normalizedId === 'NORMAL') return current;

  const nextIds = current.ids.includes(normalizedId) ? current.ids : [...current.ids, normalizedId];

  const multiplierValue = SCORE_MULTIPLIER_MODIFIER_VALUES[normalizedId];
  if (multiplierValue) {
    // Additive stacking: first modifier sets the value, subsequent ones add to it.
    // (multiplier=1 is the neutral baseline meaning "no modifier yet")
    const newMultiplier = current.multiplier <= 1 ? multiplierValue : current.multiplier + multiplierValue;
    return { ...current, multiplier: newMultiplier, ids: nextIds };
  }

  const meta = getTileModifierMeta(normalizedId);
  const scoreBonus = Number(meta?.scoreBonus);
  if (Number.isFinite(scoreBonus) && scoreBonus !== 0) {
    return { ...current, score: current.score + scoreBonus, ids: nextIds };
  }

  if (!current.extras.includes(normalizedId)) {
    return { ...current, extras: [...current.extras, normalizedId], ids: nextIds };
  }

  return { ...current, ids: nextIds };
};

export const BOARD_MODIFIER_TYPES = SHOP_OPTION_MODIFIER_TYPES.filter(
  (modifierType) => modifierType.category === 'board'
);

export const TILE_UPGRADE_MODIFIER_CHANCES = TILE_MODIFIER_TYPES.reduce((acc, type) => {
  if (type.shop.upgradeWeight > 0) {
    acc[type.id] = type.shop.upgradeWeight;
  }
  return acc;
}, {});

export const NEW_TILE_MODIFIER_CHANCES = TILE_MODIFIER_TYPES.reduce((acc, type) => {
  if (type.shop.newTileWeight > 0) {
    acc[type.id] = type.shop.newTileWeight;
  }
  return acc;
}, {});

export const GAME_MODIFIER_SHOP_CHANCES = GAME_MODIFIER_TYPES.reduce((acc, type) => {
  if (type.shop.gameWeight > 0) {
    acc[type.id] = type.shop.gameWeight;
  }
  return acc;
}, {});

export const BOARD_MODIFIER_SHOP_CHANCES = BOARD_MODIFIER_TYPES.reduce((acc, type) => {
  if (type.shop.boardWeight > 0) {
    acc[type.id] = type.shop.boardWeight;
  }
  return acc;
}, {});

// — Modifier enabled state (admin/testing override, persisted to localStorage) —

const ADMIN_ENABLED_STORAGE_KEY = 'altessera_admin_modifier_enabled';

const readEnabledFromStorage = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_ENABLED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeEnabledToStorage = (overrides) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ADMIN_ENABLED_STORAGE_KEY, JSON.stringify(overrides));
    }
  } catch { /* ignore */ }
};

let _enabledOverrides = readEnabledFromStorage();

export const isModifierEnabled = (id) => {
  const key = String(id || '').trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(_enabledOverrides, key)
    ? Boolean(_enabledOverrides[key])
    : true;
};

export const setModifierEnabled = (id, enabled) => {
  const key = String(id || '').trim().toUpperCase();
  if (!key) return;
  _enabledOverrides = { ..._enabledOverrides, [key]: Boolean(enabled) };
  writeEnabledToStorage(_enabledOverrides);
};

const filterEnabled = (map) =>
  Object.fromEntries(Object.entries(map).filter(([id]) => isModifierEnabled(id)));

export const getEnabledTileUpgradeChances = () => filterEnabled(TILE_UPGRADE_MODIFIER_CHANCES);
export const getEnabledNewTileChances = () => filterEnabled(NEW_TILE_MODIFIER_CHANCES);
export const getEnabledGameModifierChances = () => filterEnabled(GAME_MODIFIER_SHOP_CHANCES);
export const getEnabledBoardModifierChances = () => filterEnabled(BOARD_MODIFIER_SHOP_CHANCES);

// — Shop kind enabled state (admin/testing override, persisted to localStorage) —

const ADMIN_SHOP_KIND_ENABLED_KEY = 'altessera_admin_shop_kind_enabled';

const readShopKindEnabledFromStorage = () => {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_SHOP_KIND_ENABLED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeShopKindEnabledToStorage = (overrides) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ADMIN_SHOP_KIND_ENABLED_KEY, JSON.stringify(overrides));
    }
  } catch { /* ignore */ }
};

let _shopKindEnabledOverrides = readShopKindEnabledFromStorage();

export const isShopKindEnabled = (id) => {
  const key = String(id || '').trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(_shopKindEnabledOverrides, key)
    ? Boolean(_shopKindEnabledOverrides[key])
    : true;
};

export const setShopKindEnabled = (id, enabled) => {
  const key = String(id || '').trim().toUpperCase();
  if (!key) return;
  _shopKindEnabledOverrides = { ..._shopKindEnabledOverrides, [key]: Boolean(enabled) };
  writeShopKindEnabledToStorage(_shopKindEnabledOverrides);
};

export const TOGGLEABLE_SHOP_KINDS = [
  {
    id: 'REMOVE_BAG_TILE',
    label: 'Tile Removal',
    description: 'Offers a shop option to permanently remove a tile from your bag.',
  },
];

export const resolveModifierGrantedEffects = (modifierId, context = {}) => {
  const meta = getShopOptionModifierMeta(modifierId);
  if (!meta?.grantsGameEffects) {
    return [];
  }

  if (typeof meta.grantsGameEffects === 'function') {
    const resolved = meta.grantsGameEffects(context);
    return Array.isArray(resolved) ? resolved : [];
  }

  return Array.isArray(meta.grantsGameEffects) ? meta.grantsGameEffects : [];
};

export const DEFAULT_TILE_MODIFIER_ASSIGNMENTS = {
  A: { score: 1, multiplier: 1, extras: [] },
  E: { score: 1, multiplier: 1, extras: [] },
  I: { score: 1, multiplier: 1, extras: [] },
  O: { score: 1, multiplier: 1, extras: [] },
  U: { score: 1, multiplier: 1, extras: [] },
};
